import React, { useState, useMemo, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import CodeIcon from '@mui/icons-material/Code';
import { artifactService } from '../../services/artifactService';
import apiClient from '../../services/api';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Custom high-contrast style for better readability
const customHighContrastStyle = {
  ...prism,
  'code[class*="language-"]': {
    ...prism['code[class*="language-"]'],
    color: '#000000', // Black text
    background: '#ffffff', // White background
  },
  'pre[class*="language-"]': {
    ...prism['pre[class*="language-"]'],
    color: '#000000', // Black text
    background: '#f8f9fa', // Light gray background
    border: '1px solid #e9ecef',
    borderRadius: '4px',
  },
  'token.comment': {
    color: '#6a737d', // Gray for comments
  },
  'token.string': {
    color: '#032f62', // Dark blue for strings
  },
  'token.number': {
    color: '#005cc5', // Blue for numbers
  },
  'token.boolean': {
    color: '#d73a49', // Red for booleans
  },
  'token.keyword': {
    color: '#d73a49', // Red for keywords
  },
  'token.function': {
    color: '#6f42c1', // Purple for functions
  },
  'token.property': {
    color: '#005cc5', // Blue for properties
  },
  'token.punctuation': {
    color: '#24292e', // Dark gray for punctuation
  },
};

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  minHeight: '400px',
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(1),
  backgroundColor: '#ffffff',
  overflow: 'auto', // Allow scrolling when content is too large
  flex: 1,
  width: '100%',
  boxSizing: 'border-box',
  '& > *': {
    minWidth: 0,
    width: '100%', // Ensure all children take full width
    boxSizing: 'border-box',
  },
}));

const FileContainer = styled('div')(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'auto',
  borderRadius: theme.shape.borderRadius,
}));

const ImagePreview = styled('img')(({ theme }) => ({
  maxWidth: '100%',
  maxHeight: '800px', // Increased for better visibility
  objectFit: 'contain',
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
}));

const PDFPreview = styled('iframe')(({ theme }) => ({
  width: '100%',
  height: '800px', // Increased height for better visibility
  border: 'none',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: '#fff',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
}));

const TextPreview = styled('pre')(({ theme }) => ({
  width: '100%',
  maxHeight: '70vh', // Use viewport height instead of fixed pixels
  overflow: 'auto',
  padding: '20px',
  fontSize: '14px',
  lineHeight: '1.5',
  backgroundColor: '#ffffff', // Clean white background
  color: '#212529', // Dark text for readability
  borderRadius: '8px',
  margin: 0,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  border: '1px solid #e0e0e0',
  fontFamily: '"Roboto Mono", "Monaco", "Consolas", monospace',
  // Fix text rendering issues
  textRendering: 'optimizeLegibility',
  WebkitFontSmoothing: 'antialiased',
  MozOsxFontSmoothing: 'grayscale',
  // Ensure content doesn't get clipped
  boxSizing: 'border-box',
  minWidth: 0, // Prevent flex shrinking issues
  // Custom scrollbar for better UX
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '4px',
    '&:hover': {
      background: '#a8a8a8',
    },
  },
}));

interface FileViewerProps {
  open: boolean;
  onClose: () => void;
  fileUrl: string;
  fileName: string;
  fileType: string;
}

const FileViewer: React.FC<FileViewerProps> = ({
  open,
  onClose,
  fileUrl,
  fileName,
  fileType,
}) => {
  const [loading, setLoading] = useState(false);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isImage = artifactService.isImageFile(fileType);
  const isPdf = artifactService.isPdfFile(fileType);
  const isText = artifactService.isTextFile(fileType);
  const isMarkdown = artifactService.isMarkdownFile(fileType);
  
  // State for blob URL management
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [fileBlob, setFileBlob] = useState<Blob | null>(null);

  // Memoized content processing for better performance with large markdown files
  const processedMarkdownContent = useMemo(() => {
    if (!isMarkdown || !textContent) return null;
    
    let cleanedContent = textContent;

    // Remove leading ```markdown and trailing ``` if present
    if (cleanedContent.trim().startsWith('```markdown')) {
      cleanedContent = cleanedContent.replace(/^```markdown\s*\n?/, '').replace(/\n?```\s*$/, '');
    } else if (cleanedContent.trim().startsWith('```md')) {
      cleanedContent = cleanedContent.replace(/^```md\s*\n?/, '').replace(/\n?```\s*$/, '');
    } else if (cleanedContent.trim().startsWith('```')) {
      // Handle generic code blocks that might contain markdown
      const firstNewline = cleanedContent.indexOf('\n');
      if (firstNewline > 0) {
        const firstLine = cleanedContent.substring(0, firstNewline).trim();
        if (firstLine === '```' || firstLine.startsWith('```')) {
          cleanedContent = cleanedContent.replace(/^```[a-z]*\s*\n?/, '').replace(/\n?```\s*$/, '');
        }
      }
    }
    
    // Remove or neutralize patterns that might create unwanted checkboxes
    // This addresses the dev environment checkbox issue
    cleanedContent = cleanedContent
      // Remove task list patterns that create checkboxes
      .replace(/^\s*-\s*\[\s*\]\s*/gm, '- ') // Unchecked task lists: - [ ] 
      .replace(/^\s*-\s*\[x\]\s*/gm, '- ') // Checked task lists: - [x]
      .replace(/^\s*\*\s*\[\s*\]\s*/gm, '* ') // Unchecked with asterisk: * [ ]
      .replace(/^\s*\*\s*\[x\]\s*/gm, '* ') // Checked with asterisk: * [x]
      // Remove standalone checkbox patterns
      .replace(/\[\s*\]/g, '') // Remove empty checkboxes
      .replace(/\[x\]/g, '') // Remove checked checkboxes
      .replace(/\[X\]/g, ''); // Remove uppercase checked checkboxes
    
    return cleanedContent;
  }, [isMarkdown, textContent]);

  // Memoized download handler to prevent unnecessary re-renders
  const handleDownload = useCallback(async () => {
    try {
      if (fileBlob) {
        // Use existing blob if available
        const url = URL.createObjectURL(fileBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        // Fetch file via authenticated API and download
        const response = await apiClient.getBlob(fileUrl.replace(apiClient.getBaseUrl(), ''));
        const url = URL.createObjectURL(response);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      setError('Failed to download file. Please try again.');
    }
  }, [fileBlob, fileUrl, fileName]);

  // Memoized handler for opening in new tab
  const handleOpenInNewTab = useCallback(() => {
    if (blobUrl) {
      window.open(blobUrl, '_blank');
    } else {
      setError('File not loaded. Please wait for the file to load first.');
    }
  }, [blobUrl]);

  // Memoized file loading to prevent flickering and excessive re-renders
  const loadFileContent = useCallback(async () => {
    if (!open || loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const baseUrl = apiClient.getBaseUrl();
      const endpointPath = fileUrl.replace(baseUrl, '');
      
      if (isText || isMarkdown) {
        // For text/markdown files, load as text for display
        const content = await apiClient.get<string>(endpointPath, { 
          responseType: 'text' as any 
        });
        setTextContent(content);
        
        // Also create blob for download
        const blob = new Blob([content], { type: fileType });
        setFileBlob(blob);
      } else {
        // For binary files (images, PDFs, etc.), load as blob
        const blob = await apiClient.getBlob(endpointPath);
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        setFileBlob(blob);
      }
    } catch (err) {
      console.error('Error loading file:', err);
      setError('Failed to load file content.');
    } finally {
      setLoading(false);
    }
  }, [open, fileUrl, isText, isMarkdown, fileType, loading]);

  // Load file content when dialog opens - single effect to prevent flickering
  React.useEffect(() => {
    if (open && !textContent && !blobUrl && !loading) {
      loadFileContent();
    }
  }, [open, textContent, blobUrl, loading, loadFileContent]);

  // Reset state when dialog closes to prevent flickering on re-open
  React.useEffect(() => {
    if (!open) {
      setTextContent(null);
      setError(null);
      setLoading(false);
      // Keep blobUrl for potential re-use but reset text content
    }
  }, [open]);

  // Cleanup blob URL when component unmounts or dialog closes
  React.useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  // Reset state when dialog closes
  React.useEffect(() => {
    if (!open) {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
      setFileBlob(null);
      setTextContent(null);
      setError(null);
      setLoading(false);
    }
  }, [open, blobUrl]);

  // Memoized file icon based on file type - prevents unnecessary re-renders
  const getFileIcon = useMemo(() => {
    if (isImage) return <ImageIcon fontSize="large" />;
    if (isPdf) return <PictureAsPdfIcon fontSize="large" />;
    if (isMarkdown) return <DescriptionIcon fontSize="large" />;
    if (isText) return <CodeIcon fontSize="large" />;
    return <DescriptionIcon fontSize="large" />;
  }, [isImage, isPdf, isMarkdown, isText]);

  const renderFilePreview = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="400px">
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="400px">
          <Typography color="error" gutterBottom>
            {error}
          </Typography>
          <Button variant="outlined" onClick={handleDownload} startIcon={<DownloadIcon />}>
            Download File
          </Button>
        </Box>
      );
    }

    if (isImage) {
      if (blobUrl) {
        return <ImagePreview src={blobUrl} alt={fileName} />;
      } else if (loading) {
        return (
          <Box display="flex" justifyContent="center" alignItems="center" height="200px">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>Loading image...</Typography>
          </Box>
        );
      }
    }

    if (isPdf) {
      if (blobUrl) {
        return <PDFPreview src={blobUrl} title={fileName} />;
      } else if (loading) {
        return (
          <Box display="flex" justifyContent="center" alignItems="center" height="200px">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>Loading PDF...</Typography>
          </Box>
        );
      }
    }

    if (processedMarkdownContent) {
      
      return (
        <Box sx={{ 
          padding: '24px', // Fixed padding to prevent clipping
          minHeight: '400px',
          maxHeight: '70vh', 
          overflow: 'auto',
          backgroundColor: '#ffffff',
          borderRadius: 1,
          color: '#212529',
          border: 'none', // Remove border that might cause layout issues
          width: '100%', // Ensure full width
          boxSizing: 'border-box', // Include padding in width calculation
          // Ensure content doesn't get clipped
          '& > *': {
            minWidth: 0, // Prevent flex shrinking issues
            maxWidth: '100%', // Prevent content overflow
          },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            color: '#212529', // Dark text
            borderBottom: '2px solid #e9ecef', // Light border
            paddingBottom: '8px',
            marginBottom: '16px',
          },
          '& p': {
            color: '#212529', // Dark text
            lineHeight: 1.6,
            marginBottom: '16px',
          },
          '& code': {
            backgroundColor: '#e9ecef', // Light gray background for inline code
            color: '#d63384', // Bootstrap pink for code
            padding: '2px 4px',
            borderRadius: '4px',
            fontSize: '0.875em',
          },
          '& pre': {
            backgroundColor: '#f8f9fa', // Very light gray for code blocks
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '16px',
            overflow: 'auto',
          },
          '& blockquote': {
            borderLeft: '4px solid #0d6efd', // Blue accent for blockquotes
            backgroundColor: '#f8f9fa', // Light background
            color: '#495057', // Slightly muted text
            margin: '16px 0',
            padding: '12px 16px',
            borderRadius: '0 4px 4px 0',
          },
          '& a': {
            color: '#0d6efd', // Bootstrap primary blue
            textDecoration: 'underline',
            '&:hover': {
              color: '#0a58ca', // Darker blue on hover
            },
          },
          '& table': {
            width: '100%',
            borderCollapse: 'collapse',
            marginBottom: '16px',
            '& th, & td': {
              color: '#212529', // Dark text
              border: '1px solid #dee2e6', // Light borders
              padding: '12px',
              textAlign: 'left',
            },
            '& th': {
              backgroundColor: '#f8f9fa', // Light gray headers
              fontWeight: 600,
            },
            '& tr:nth-of-type(even)': {
              backgroundColor: '#f8f9fa', // Zebra striping
            },
          },
          '& ul, & ol': {
            paddingLeft: '24px',
            marginBottom: '16px',
          },
          '& li': {
            marginBottom: '8px',
            color: '#212529',
          },
        }}>
          <div style={{
            width: '100%',
            minWidth: 0,
            wordWrap: 'break-word',
            overflowWrap: 'anywhere',
          }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
              code({ node, inline, className, children, ...props }: any) {
                const match = /language-(\w+)/.exec(className || '');
                return !inline && match ? (
                  <SyntaxHighlighter
                    style={customHighContrastStyle}
                    language={match[1]}
                    PreTag="div"
                    {...props}
                  >
                    {String(children).replace(/\n$/, '')}
                  </SyntaxHighlighter>
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              },
              // Enhanced checkbox rendering - prevent unwanted checkboxes
              input({ node, type, checked, ...props }: any) {
                if (type === 'checkbox') {
                  // For dev environment issues: completely hide unwanted checkboxes
                  // This prevents any checkbox artifacts from appearing
                  return null;
                  
                  // Alternative: Render as styled checkbox (uncomment if checkboxes are intentional)
                  /*
                  return (
                    <input
                      type="checkbox"
                      checked={checked}
                      readOnly
                      disabled
                      style={{ 
                        marginRight: '8px',
                        pointerEvents: 'none',
                        opacity: 0.7,
                        transform: 'scale(0.9)'
                      }}
                      {...props}
                    />
                  );
                  */
                }
                return <input {...props} />;
              },
              // Enhanced list item rendering - handle task lists properly
              li({ node, children, className, ...props }: any) {
                // Check if this is a task list item that might contain checkboxes
                const isTaskListItem = className && className.includes('task-list-item');
                
                return (
                  <li 
                    className={isTaskListItem ? 'task-list-item' : ''}
                    style={{ 
                      marginBottom: '8px',
                      listStylePosition: 'outside',
                      paddingLeft: isTaskListItem ? '0px' : '4px',
                      listStyle: isTaskListItem ? 'none' : 'inherit'
                    }}
                    {...props}
                  >
                    {children}
                  </li>
                );
              },
              // Fix paragraph spacing and text wrapping
              p({ node, children, ...props }: any) {
                return (
                  <p 
                    style={{
                      margin: '0 0 16px 0',
                      lineHeight: '1.6',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      hyphens: 'auto',
                      width: '100%',
                      minWidth: 0,
                    }}
                    {...props}
                  >
                    {children}
                  </p>
                );
              },
              // Fix header text wrapping
              h1: ({ node, children, ...props }: any) => (
                <h1 
                  style={{
                    margin: '0 0 24px 0',
                    lineHeight: '1.2',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    width: '100%',
                    minWidth: 0,
                  }}
                  {...props}
                >
                  {children}
                </h1>
              ),
              h2: ({ node, children, ...props }: any) => (
                <h2 
                  style={{
                    margin: '0 0 20px 0',
                    lineHeight: '1.3',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    width: '100%',
                    minWidth: 0,
                  }}
                  {...props}
                >
                  {children}
                </h2>
              ),
              h3: ({ node, children, ...props }: any) => (
                <h3 
                  style={{
                    margin: '0 0 16px 0',
                    lineHeight: '1.4',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    width: '100%',
                    minWidth: 0,
                  }}
                  {...props}
                >
                  {children}
                </h3>
              ),
              // Fix table rendering
              table({ node, children, ...props }: any) {
                return (
                  <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
                    <table 
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        minWidth: '500px'
                      }}
                      {...props}
                    >
                      {children}
                    </table>
                  </div>
                );
              },
              a: ({ node, ...props }) => <a target="_blank" rel="noopener noreferrer" {...props} />,
            }}
          >
            {processedMarkdownContent}
          </ReactMarkdown>
          </div>
        </Box>
      );
    } else if (isText && textContent) {
      return <TextPreview>{textContent}</TextPreview>;
    }

    // For other file types, show download option
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="400px">
        {getFileIcon}
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          {fileName}
        </Typography>
        <Typography color="textSecondary" gutterBottom>
          {fileType}
        </Typography>
        <Button variant="contained" onClick={handleDownload} startIcon={<DownloadIcon />} sx={{ mt: 2 }}>
          Download File
        </Button>
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xl" 
      fullWidth 
      PaperProps={{ 
        elevation: 24,
        sx: { 
          borderRadius: 2,
          backgroundColor: '#ffffff',
          color: '#000000',
          minHeight: '60vh',
          maxHeight: '95vh',
          width: '95vw',
          maxWidth: '1400px', // Increased max width for better content display
          margin: '20px',
          // Prevent content overflow and layout shifts
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          // Ensure proper sizing
          boxSizing: 'border-box',
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#1976d2', // Material-UI primary blue
        color: '#FFFFFF',
        padding: 2
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 'bold', textOverflow: 'ellipsis', overflow: 'hidden' }}>
            {fileName}
          </Typography>
          <Box>
            <IconButton 
              size="small" 
              onClick={handleDownload} 
              title="Download"
              sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' } }}
            >
              <DownloadIcon />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={handleOpenInNewTab} 
              title="Open in new tab"
              sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' } }}
            >
              <OpenInNewIcon />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={onClose}
              sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' } }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <StyledDialogContent>
        <FileContainer>{renderFilePreview()}</FileContainer>
      </StyledDialogContent>
      <DialogActions sx={{ 
        borderTop: '1px solid #dee2e6', // Light border
        padding: 2,
        backgroundColor: '#f8f9fa', // Light background
      }}>
        <Button 
          onClick={onClose} 
          variant="contained" 
          color="primary"
          sx={{ 
            textTransform: 'none', 
            fontWeight: 500,
            backgroundColor: '#1976d2', // Primary blue
            '&:hover': {
              backgroundColor: '#1565c0', // Darker blue on hover
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileViewer;